import React, { type FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { overquotaSafeVariantC } from 'reactApp/appHelpers/featuresHelpers/features/overquotaSafe';
import { sendDwhButton, sendDwhTooltip } from 'reactApp/modules/dwh/dwh.helpers';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EActions, EStorageType } from 'reactApp/modules/storage/storage.types';
import { restoreFromSafeRequest } from 'reactApp/modules/trashbin/trashbin.module';
import { isSuccessfullyStoredToSafe } from 'reactApp/modules/trashbin/trashbin.selectors';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { ETooltipPlacement, ETooltipSize } from 'reactApp/ui/FloatingTooltip/FloatingTooltip.types';
import { EToolbarItemMod } from 'reactApp/ui/Toolbar/Toolbar.types';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';

import styles from './RestoreFromSafeButton.css';

const TOOLTIP_ID = 'tooltip-restore-from-safe';

const name = 'recover_from_safe';
const nameTip = overquotaSafeVariantC ? 'files_to_safe_no_del' : 'files_to_safe';

export const RestoreFromSafeButton: FC = memo(({ ...rest }) => {
    const dispatch = useDispatch();
    const ref = useRef<HTMLDivElement>(null);
    const [showTooltip, setShowTooltip] = useState(false);
    const isStoredToSafe = useSelector(isSuccessfullyStoredToSafe);
    const storage = useSelector(getCurrentStorage);
    const wasShown = storeHelper.getValue(TOOLTIP_ID);
    const periodPassed = wasShown && storeHelper.isPassed(TOOLTIP_ID, { daysCount: 1 });
    const source = storage === EStorageType.trashbin ? 'trashbin' : 'cleaner';

    const showTip = (!wasShown || periodPassed) && isStoredToSafe && showTooltip;

    useEffect(() => {
        if (isStoredToSafe) {
            setShowTooltip(true);
        }
    }, [isStoredToSafe]);

    useEffect(() => {
        sendDwhButton({ name, source });
    }, []);

    const handleClick = useCallback(() => {
        sendDwhButton({ action: 'click', name, source });
        dispatch(restoreFromSafeRequest());
    }, []);

    const handleTipShow = useCallback(() => {
        sendDwhTooltip({ name: nameTip, place: source });
    }, []);

    const handleTipClick = useCallback(() => {
        sendDwhTooltip({ action: 'click', name: nameTip, place: source });
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    const handleTipClose = useCallback(() => {
        sendDwhTooltip({ action: 'close', name: nameTip, place: source });
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    const handleClose = useCallback(() => {
        storeHelper.markAsShown(TOOLTIP_ID);
        setShowTooltip(false);
    }, []);

    return (
        <>
            <ToolbarItem
                ref={ref}
                onClick={handleClick}
                id={EActions.restoreSafe}
                text="Вернуть из сейфа"
                mod={EToolbarItemMod.blueOutline}
                {...rest}
            />
            {showTip && (
                <FloatingTooltip
                    target={ref}
                    className={styles.tip}
                    contentClassName={styles.tipContent}
                    placement={ETooltipPlacement.bottom}
                    size={ETooltipSize.large}
                    title={storage === EStorageType.trashbin ? 'Файлы из корзины — в сейфе' : 'Ваши файлы — в сейфе'}
                    text={
                        <div className={styles.tipText}>
                            Они не занимают место в Облаке.
                            <br />
                            Их можно вернуть, если будут нужны
                        </div>
                    }
                    qaId={TOOLTIP_ID}
                    buttonText="Хорошо"
                    buttonFitContent
                    closeOnOutsideClick={true}
                    onCrossClose={handleTipClose}
                    onClose={handleClose}
                    onClick={handleTipClick}
                    onShow={handleTipShow}
                />
            )}
        </>
    );
});

RestoreFromSafeButton.displayName = 'RestoreFromSafeButton';
